import { apiFetch } from "./client";

export type ProfessionMetier = {
  slug: string;
  title: string;
};

export type BeninDepartement = {
  id: string;
  nom: string;
  chef_lieu?: string;
  nbCommunes?: number;
};

export type BeninCommune = {
  id?: string;
  nom: string;
};

export type BeninArrondissement = {
  id?: string;
  nom: string;
};

function toList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const bag = raw as Record<string, unknown>;
    if (Array.isArray(bag.data)) return bag.data;
    if (bag.data && typeof bag.data === "object" && !Array.isArray(bag.data)) {
      const inner = bag.data as Record<string, unknown>;
      for (const key of ["items", "list", "results", "rows"] as const) {
        if (Array.isArray(inner[key])) return inner[key] as unknown[];
      }
    }
  }
  return [];
}

function normalizeNom(value: unknown): string {
  return String(value ?? "").trim();
}

function mapDepartements(raw: unknown): BeninDepartement[] {
  const result: BeninDepartement[] = [];
  for (const row of toList(raw)) {
    const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const id = String(o.id ?? o._id ?? o.code ?? "").trim();
    const nom = normalizeNom(o.nom ?? o.name ?? o.libelle);
    if (!id || !nom) continue;
    result.push({
      id,
      nom,
      chef_lieu: normalizeNom(o.chef_lieu ?? o.chefLieu) || undefined,
      nbCommunes: typeof o.nbCommunes === "number" ? o.nbCommunes : undefined,
    });
  }
  return result;
}

function mapSimpleNamedList(raw: unknown): Array<{ id?: string; nom: string }> {
  return toList(raw)
    .map((row) => {
      if (typeof row === "string") {
        const nom = row.trim();
        return nom ? { nom } : null;
      }
      const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
      const nom = normalizeNom(o.nom ?? o.name ?? o.libelle);
      if (!nom) return null;
      const id = String(o.id ?? o._id ?? "").trim() || undefined;
      return { id, nom };
    })
    .filter((item): item is { id?: string; nom: string } => Boolean(item));
}

export async function getBeninDepartements(): Promise<
  { ok: true; data: BeninDepartement[] } | { ok: false; message: string; status?: number }
> {
  const out = await apiFetch<unknown>("/public/benin/departements", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapDepartements(out.data) };
}

export async function getBeninCommunes(
  departementId: string
): Promise<{ ok: true; data: BeninCommune[] } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(
    `/public/benin/departements/${encodeURIComponent(departementId)}/communes`,
    { skipAuth: true }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapSimpleNamedList(out.data) };
}

export async function getBeninArrondissements(
  departementId: string,
  nomCommune: string
): Promise<{ ok: true; data: BeninArrondissement[] } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(
    `/public/benin/departements/${encodeURIComponent(
      departementId
    )}/communes/${encodeURIComponent(nomCommune)}/arrondissements`,
    { skipAuth: true }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapSimpleNamedList(out.data) };
}

function mapProfessionsMetiers(raw: unknown): ProfessionMetier[] {
  const result: ProfessionMetier[] = [];
  for (const row of toList(raw)) {
    const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const slug = String(o.slug ?? "").trim();
    const title = String(o.title ?? o.label ?? o.nom ?? "").trim();
    if (!slug || !title) continue;
    result.push({ slug, title });
  }
  // "Autre" doit toujours être la dernière option d'un menu déroulant, jamais mélangée dans le
  // tri alphabétique par titre que fait le backend.
  result.sort((a, b) => (a.slug === "autre" ? 1 : 0) - (b.slug === "autre" ? 1 : 0));
  return result;
}

export async function getProfessionsMetiers(): Promise<
  { ok: true; data: ProfessionMetier[] } | { ok: false; message: string; status?: number }
> {
  const out = await apiFetch<unknown>("/public/professions-metiers", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapProfessionsMetiers(out.data) };
}

function mapMaladiesEpidemiques(raw: unknown): string[] {
  const result: string[] = [];
  for (const row of toList(raw)) {
    const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const nom = normalizeNom(o.nom);
    if (nom) result.push(nom);
  }
  return result;
}

/**
 * Référentiel officiel de surveillance épidémiologique du Bénin (~12 maladies actives) — sert de
 * liste contrôlée pour le champ "Diagnostic / Maladie" en clôture de consultation, avec repli
 * "Autre" en texte libre pour tout diagnostic hors de ce référentiel.
 */
export async function getMaladiesEpidemiques(): Promise<
  { ok: true; data: string[] } | { ok: false; message: string; status?: number }
> {
  const out = await apiFetch<unknown>("/public/maladies-epidemiques", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapMaladiesEpidemiques(out.data) };
}

export type Pharmacie = {
  id: string;
  nomPharmacie: string;
  mail?: string;
  tel?: string;
  departement?: string;
  commune?: string;
  quartier?: string;
};

function mapPharmacies(raw: unknown): Pharmacie[] {
  const result: Pharmacie[] = [];
  for (const row of toList(raw)) {
    const o = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    const id = String(o.id ?? "").trim();
    const nomPharmacie = String(o.nomPharmacie ?? o.nom ?? "").trim();
    if (!id || !nomPharmacie) continue;
    result.push({
      id,
      nomPharmacie,
      mail: String(o.mail ?? o.email ?? "").trim() || undefined,
      tel: String(o.tel ?? o.telephone ?? "").trim() || undefined,
      departement: String(o.departement ?? "").trim() || undefined,
      commune: String(o.commune ?? "").trim() || undefined,
      quartier: String(o.quartier ?? "").trim() || undefined,
    });
  }
  return result;
}

export async function getPublicPharmacies(params?: {
  departement?: string;
  commune?: string;
  limit?: number;
  offset?: number;
}): Promise<{ ok: true; data: Pharmacie[] } | { ok: false; message: string; status?: number }> {
  const q = new URLSearchParams();
  if (params?.departement) q.set("departement", params.departement);
  if (params?.commune) q.set("commune", params.commune);
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  const qs = q.toString();
  const out = await apiFetch<unknown>(`/public/pharmacies${qs ? "?" + qs : ""}`, { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: mapPharmacies(out.data) };
}
